/**
 * Permanent Clarity commercial service catalogue items.
 * Always available in Retail + Project catalogues without D-Tools import.
 */
import {
  EQUIPMENT_PRICING_METHOD_FIXED,
  EQUIPMENT_PRICING_METHOD_PERCENTAGE,
  isClarityBrand,
  type EquipmentPricingMethod,
} from "./equipmentPercentagePricing";

export const CLARITY_SERVICE_CATEGORY = "Services";

export type ClarityStandardServiceDefinition = {
  /** Stable SKU / catalogue key fragment — used for dedupe. */
  sku: string;
  /** Model / short name shown in catalogue. */
  model: string;
  /** Full customer-facing name. */
  name: string;
  displayName: string;
  description: string;
  /** Stable project-catalogue catalogueKey. */
  catalogueKey: string;
};

export const CLARITY_STANDARD_SERVICE_ITEMS: readonly ClarityStandardServiceDefinition[] = [
  {
    sku: "CLARITY-SVC-PM",
    model: "Project Management",
    name: "Clarity Project Management",
    displayName: "Clarity Project Management",
    description: "Clarity project management for delivery coordination and commercial oversight.",
    catalogueKey: "clarity-svc-project-management",
  },
  {
    sku: "CLARITY-SVC-PROG",
    model: "Programming",
    name: "Clarity Programming",
    displayName: "Clarity Programming",
    description: "Clarity control-system programming and configuration.",
    catalogueKey: "clarity-svc-programming",
  },
  {
    sku: "CLARITY-SVC-IC",
    model: "Installation and Commissioning",
    name: "Clarity Installation and Commissioning",
    displayName: "Clarity Installation and Commissioning",
    description: "Clarity installation, testing and commissioning services.",
    catalogueKey: "clarity-svc-installation-commissioning",
  },
] as const;

function clean(value: unknown): string {
  return String(value ?? "")
    .trim()
    .replace(/\s+/g, " ");
}

function norm(value: unknown): string {
  return clean(value).toLowerCase();
}

const NAME_ALIASES = new Map<string, string>([
  ["clarity project management", "CLARITY-SVC-PM"],
  ["project management", "CLARITY-SVC-PM"],
  ["clarity project management fees", "CLARITY-SVC-PM"],
  ["clarity programming", "CLARITY-SVC-PROG"],
  ["programming", "CLARITY-SVC-PROG"],
  ["clarity installation and commissioning", "CLARITY-SVC-IC"],
  ["installation and commissioning", "CLARITY-SVC-IC"],
  ["clarity installation, testing & commissioning", "CLARITY-SVC-IC"],
  ["clarity installation testing & commissioning", "CLARITY-SVC-IC"],
  ["clarity installation, testing and commissioning", "CLARITY-SVC-IC"],
  ["clarity installation testing and commissioning", "CLARITY-SVC-IC"],
]);

export function matchClarityStandardServiceSku(input: {
  brand?: string | null;
  manufacturer?: string | null;
  model?: string | null;
  name?: string | null;
  displayName?: string | null;
  sku?: string | null;
  catalogueKey?: string | null;
}): string | null {
  const brand = input.brand ?? input.manufacturer;
  if (!isClarityBrand(brand)) return null;

  const sku = clean(input.sku).toUpperCase();
  if (sku && CLARITY_STANDARD_SERVICE_ITEMS.some((item) => item.sku === sku)) return sku;

  const catalogueKey = norm(input.catalogueKey);
  const byKey = CLARITY_STANDARD_SERVICE_ITEMS.find((item) => item.catalogueKey === catalogueKey);
  if (byKey) return byKey.sku;

  const candidates = [input.displayName, input.name, input.model]
    .map(norm)
    .filter(Boolean);
  for (const candidate of candidates) {
    const matched = NAME_ALIASES.get(candidate);
    if (matched) return matched;
    // Strip leading "clarity "
    if (candidate.startsWith("clarity ")) {
      const stripped = NAME_ALIASES.get(candidate.slice("clarity ".length));
      if (stripped) return stripped;
    }
  }
  return null;
}

export function isClarityStandardServiceProduct(input: {
  brand?: string | null;
  manufacturer?: string | null;
  model?: string | null;
  name?: string | null;
  displayName?: string | null;
  sku?: string | null;
  catalogueKey?: string | null;
}): boolean {
  return matchClarityStandardServiceSku(input) != null;
}

export function clarityStandardServiceBySku(sku: string): ClarityStandardServiceDefinition | null {
  return CLARITY_STANDARD_SERVICE_ITEMS.find((item) => item.sku === sku) ?? null;
}

/** Staff-facing pricing mode label for revise UI. */
export function clarityServicePricingModeLabel(
  pricingMethod: unknown,
  percentageOfEquipment?: number | null,
): string {
  if (String(pricingMethod ?? "").toLowerCase() === EQUIPMENT_PRICING_METHOD_PERCENTAGE) {
    const pct = percentageOfEquipment != null ? Number(percentageOfEquipment) : null;
    if (pct != null && Number.isFinite(pct)) return `${pct}% of Hardware`;
    return "Percentage of Hardware";
  }
  return "Flat Fee";
}

export type ClarityServicePricingChoice =
  | { mode: typeof EQUIPMENT_PRICING_METHOD_FIXED; flatAmount: number }
  | { mode: typeof EQUIPMENT_PRICING_METHOD_PERCENTAGE; percentage: number };

export function resolveClarityServicePricingFields(
  choice: ClarityServicePricingChoice,
): {
  pricingMethod: EquipmentPricingMethod;
  percentageOfEquipment: number | null;
  unitPrice: number;
  quantity: number;
  lineTotal: number;
} {
  if (choice.mode === EQUIPMENT_PRICING_METHOD_PERCENTAGE) {
    const percentage = Math.max(0, Number(choice.percentage) || 0);
    return {
      pricingMethod: EQUIPMENT_PRICING_METHOD_PERCENTAGE,
      percentageOfEquipment: percentage,
      unitPrice: 0,
      quantity: 1,
      lineTotal: 0,
    };
  }
  const flatAmount = Math.max(0, Number(choice.flatAmount) || 0);
  return {
    pricingMethod: EQUIPMENT_PRICING_METHOD_FIXED,
    percentageOfEquipment: null,
    unitPrice: flatAmount,
    quantity: 1,
    lineTotal: flatAmount,
  };
}

/** Merge a Flat Fee / Percentage choice onto a catalogue product pick before insert. */
export function applyClarityServicePricingToProductPick<
  T extends {
    unitPrice: number;
    pricingMethod?: EquipmentPricingMethod | null;
    percentageOfEquipment?: number | null;
  },
>(product: T, choice: ClarityServicePricingChoice): T {
  const fields = resolveClarityServicePricingFields(choice);
  return {
    ...product,
    unitPrice: fields.unitPrice,
    pricingMethod: fields.pricingMethod,
    percentageOfEquipment: fields.percentageOfEquipment,
  };
}
